import { z } from 'zod';
/**
 * Kostenstruktur und Tragfähigkeit (KON-256). Wirkia legt die Einnahmen und Ausgaben der letzten zwölf Monate, die Personalkosten, die festen Kosten und die bestehenden Finanzierungen
 * nebeneinander und prüft, ob eine geplante neue Rate getragen wird. Alles hier ist rein gerechnet (ohne Datenbank), damit die Oberfläche, der Bericht und die Tests dieselben Zahlen sehen.
 * Alle Beträge in Cent, Monatswerte sind Durchschnitte über den beobachteten Zeitraum.
 */
// ---------------------------------------------------------------------------
// Feste Kosten aus den Sachkonten der Eingangsrechnungen
// ---------------------------------------------------------------------------
export const FIXED_COST_GROUPS = ['premises', 'communication', 'insurance', 'vehicle_fixed', 'advisory', 'software'];
export const FIXED_COST_GROUP_LABELS = {
    premises: 'Raumkosten (Miete, Pacht, Nebenkosten, Energie)',
    communication: 'Telefon, Internet und Porto',
    insurance: 'Versicherungen und Beiträge',
    vehicle_fixed: 'Fahrzeug: Versicherung und Steuer',
    advisory: 'Steuerberatung und Buchführung',
    software: 'Software und Lizenzen',
};
/**
 * Welche Sachkonten als feste Kosten zählen, ergibt sich aus der Bezeichnung des Kontos und seiner Kategorie im Kontenplan. Die Reihenfolge entscheidet: „Kfz-Versicherung“ ist eine
 * Fahrzeugkost und keine allgemeine Versicherung. Kraftstoff, Wartung, Waren und alles, was hier nicht passt, gelten als veränderliche Kosten.
 */
const FIXED_COST_RULES = [
    { group: 'vehicle_fixed', pattern: /kfz[- ]?(versicherung|steuer)|fahrzeug[- ]?(versicherung|steuer)|kraftfahrzeug(versicherung|steuer)/ },
    { group: 'insurance', pattern: /versicherung|beitr[aä]ge/ },
    { group: 'premises', pattern: /raumkosten|miete|pacht|nebenkosten|heizung|strom|energie|wasser|gas\b|reinigung|hausverwaltung/ },
    { group: 'communication', pattern: /telefon|internet|kommunikation|porto|mobilfunk|telekommunikation/ },
    { group: 'advisory', pattern: /steuerberat|buchf[uü]hrung|buchfuehrung|jahresabschluss|rechts- und beratung|rechtsberatung/ },
    { group: 'software', pattern: /software|lizenz|edv|cloud|hosting/ },
];
/** Gruppe der festen Kosten für ein Konto, null = veränderliche Kosten. Zuerst zählt der Name des Kontos, dann der Pfad seiner Kategorien. */
export function fixedCostGroupOf(accountName, categoryPath = '') {
    const name = accountName.toLowerCase();
    const path = categoryPath.toLowerCase();
    for (const text of [name, path]) {
        const rule = FIXED_COST_RULES.find((r) => r.pattern.test(text));
        if (rule)
            return rule.group;
    }
    return null;
}
// ---------------------------------------------------------------------------
// Personalkosten
// ---------------------------------------------------------------------------
/** Arbeitgeberanteile (Sozialversicherung, Umlagen) als Zuschlag auf das Bruttoentgelt, in Basispunkten. Richtwerte, der Mandant kann sie in den Einstellungen ändern. */
export const DEFAULT_EMPLOYER_ON_COST_BP = 2100;
/** Minijob: Pauschalabgaben des Arbeitgebers und Umlagen */
export const DEFAULT_MINIJOB_ON_COST_BP = 3100;
export const PERSONNEL_COST_KINDS = ['accident_insurance', 'training', 'workwear', 'benefits', 'recruiting', 'other'];
export const PERSONNEL_COST_KIND_LABELS = {
    accident_insurance: 'Berufsgenossenschaft (Unfallversicherung)',
    training: 'Aus- und Weiterbildung',
    workwear: 'Arbeitskleidung',
    benefits: 'Zuschüsse und Zusatzleistungen (zum Beispiel Fahrkarte, Essen)',
    recruiting: 'Personalsuche',
    other: 'Sonstige Personalkosten',
};
export const PERSONNEL_COST_PERIODS = ['month', 'year'];
export const PERSONNEL_COST_PERIOD_LABELS = { month: 'pro Monat', year: 'pro Jahr' };
const isoDate = z.iso.date('Bitte ein Datum angeben');
const nullableText = (max) => z.string().trim().max(max).nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null));
/** Weitere Personalkosten neben dem Lohn: Berufsgenossenschaft, Weiterbildung, Arbeitskleidung und Ähnliches. Mit `validUntil` lässt sich eine Kostenart befristen. */
export const personnelCostInput = z
    .object({
    kind: z.enum(PERSONNEL_COST_KINDS, 'Bitte die Art wählen'),
    label: z.string('Bitte eine Bezeichnung angeben').trim().min(2, 'Bitte eine Bezeichnung angeben').max(120),
    amountCents: z.number('Bitte den Betrag angeben').int().min(0, 'Der Betrag darf nicht negativ sein').max(100_000_000),
    period: z.enum(PERSONNEL_COST_PERIODS, 'Bitte monatlich oder jährlich wählen').default('month'),
    validFrom: isoDate,
    validUntil: isoDate.nullable().optional(),
    note: nullableText(500),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.validUntil && v.validUntil < v.validFrom)
        ctx.addIssue({ code: 'custom', path: ['validUntil'], message: 'Das Ende liegt vor dem Beginn' });
});
export const monthlyOfCost = (amountCents, period) => (period === 'year' ? Math.round(amountCents / 12) : amountCents);
/** Einstellungen der Kostenrechnung: Zuschläge auf den Bruttolohn */
export const financingSettingsInput = z
    .object({
    employerOnCostBp: z.number('Bitte den Zuschlag angeben').int().min(0, 'Nicht negativ').max(6000, 'Höchstens 60 %').optional(),
    minijobOnCostBp: z.number('Bitte den Zuschlag angeben').int().min(0, 'Nicht negativ').max(6000, 'Höchstens 60 %').optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
/** Personalkosten je Monat: Bruttoentgelte plus Arbeitgeberanteile (Minijobs mit eigenem Satz) plus die weiteren Personalkosten */
export function personnelMonthly(input) {
    let gross = 0;
    let onCost = 0;
    let missing = 0;
    for (const e of input.employees) {
        if (e.grossMonthlyCents === null) {
            missing += 1;
            continue;
        }
        gross += e.grossMonthlyCents;
        onCost += Math.round((e.grossMonthlyCents * (e.employmentKind === 'mini_job' ? input.minijobOnCostBp : input.employerOnCostBp)) / 10_000);
    }
    return {
        employeeCount: input.employees.length,
        employeesWithoutPay: missing,
        grossMonthlyCents: gross,
        onCostMonthlyCents: onCost,
        extraMonthlyCents: input.extraMonthlyCents,
        totalMonthlyCents: gross + onCost + input.extraMonthlyCents,
        employerOnCostBp: input.employerOnCostBp,
        minijobOnCostBp: input.minijobOnCostBp,
    };
}
// ---------------------------------------------------------------------------
// Tragfähigkeit
// ---------------------------------------------------------------------------
export const AFFORDABILITY_RATINGS = ['good', 'tight', 'insufficient'];
export const AFFORDABILITY_LABELS = {
    good: 'Trägt sich',
    tight: 'Knapp',
    insufficient: 'Trägt sich nicht',
};
/** Kapitaldienstdeckung in Prozent: ab diesem Wert gilt die Belastung als tragfähig, darunter bis 100 als knapp. Banken verlangen je nach Branche etwa 120 bis 150 %. */
export const COVERAGE_GOOD_PERCENT = 130;
const fmt = (cents) => `${(cents / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
/**
 * Prüft, ob die neue Monatsrate neben den bestehenden Raten getragen wird. Die Kapitaldienstdeckung (Überschuss vor Raten geteilt durch alle Raten) ist die Zahl, an der sich Banken
 * orientieren. Ab 130 % gilt die Belastung als tragfähig, zwischen 100 und 130 % als knapp, darunter nicht. Ohne Überschuss vor den Raten trägt sich keine Rate.
 */
export function assessAffordability(input) {
    const { surplusBeforeDebtCents: surplus, existingDebtServiceMonthlyCents: existing, newMonthlyCents: added, revenueMonthlyCents: revenue } = input;
    const total = existing + added;
    const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : null);
    const coverageBefore = pct(surplus, existing);
    const coverageAfter = pct(surplus, total);
    const burden = revenue > 0 ? Math.round((total / revenue) * 1000) / 10 : null;
    const surplusAfter = surplus - total;
    let rating;
    let summary;
    if (total === 0) {
        rating = 'good';
        summary = 'Es gibt weder bestehende noch neue Raten.';
    }
    else if (surplus <= 0) {
        rating = 'insufficient';
        summary = `Nach Abzug aller Kosten bleibt im Monat kein Überschuss (${fmt(surplus)}), eine Rate von ${fmt(added)} lässt sich daraus nicht zahlen.`;
    }
    else if ((coverageAfter ?? 0) >= COVERAGE_GOOD_PERCENT) {
        rating = 'good';
        summary = `Der Überschuss von ${fmt(surplus)} im Monat deckt alle Raten (${fmt(total)}) zu ${coverageAfter} %. Nach den Raten bleiben ${fmt(surplusAfter)}.`;
    }
    else if ((coverageAfter ?? 0) >= 100) {
        rating = 'tight';
        summary = `Der Überschuss von ${fmt(surplus)} im Monat deckt alle Raten (${fmt(total)}) nur zu ${coverageAfter} %. Es bleibt wenig Spielraum (${fmt(surplusAfter)} im Monat).`;
    }
    else {
        rating = 'insufficient';
        summary = `Der Überschuss von ${fmt(surplus)} im Monat reicht für alle Raten (${fmt(total)}) nicht aus, es fehlen ${fmt(-surplusAfter)} im Monat.`;
    }
    return {
        newMonthlyCents: added,
        existingDebtServiceMonthlyCents: existing,
        surplusBeforeDebtCents: surplus,
        surplusAfterCents: surplusAfter,
        coverageBeforePercent: coverageBefore,
        coverageAfterPercent: coverageAfter,
        burdenAfterPercent: burden,
        rating,
        summary,
    };
}
//# sourceMappingURL=assessment.js.map